import { RequiredProvisionModalProps } from './types';
import {
  useProvisionModalFields,
  UseProvisionModalFieldsProps,
} from './hooks/useProvisionModalFields';
import { BaseProvisionModal } from './BaseProvisionModal';
import { INTEGRATION_SERVICE_NAMES } from '@constants/integration';
import { PasswordField } from './Fields/PasswordField';
import { DataBaseNameField } from './Fields/DatabaseNameField';
import { AdvancedFields } from './Fields/AdvancedFields';
import { ProvisionModalResponseSection } from './Fields/ProvisionModalResponseSection';
import { ProvisionSection } from './Fields/ProvisionSection';
import { UsernameField } from './Fields/UsernameField';
import { FieldsContainer, ModalBodyFieldContainer } from './styles';
import { useArrayTagsInput } from 'hooks/useArrayItems/useArrayTagsInput';
import { Box } from '@mui/material';
import { TagsListSectionWithInput } from 'components/TagsListSectionWithInput';
import { ProvisionIntegrationSuccessResponse } from 'IntegrationModule/api/provisionIntegrationApi';

const targetService = INTEGRATION_SERVICE_NAMES.VIEWPOINT_VISTA_HOSTED;

export const ViewpointVistaProvisionModal: React.FC<RequiredProvisionModalProps> = ({
  teamId,
  toggle,
}) => {
  const {
    addTag: addCompanyId,
    removeTag: removeCompanyId,
    inputRef: companyListRef,
    tagsList: companyIds,
  } = useArrayTagsInput();

  const formatProvisionPayload: UseProvisionModalFieldsProps['formatProvisionPayload'] = ({
    inputValues,
  }) => {
    const paramsOnAgent = {
      serviceName: targetService,
      credentials: {
        username: inputValues.username,
        password: inputValues.password,
        database: inputValues.databaseName,
      },
      humanFriendlySetting: {
        agentSettings: {
          companyList: companyIds.map(Number),
        },
      },
    };

    return {
      serverUrl: inputValues.serverAddress,
      paramsOnAgent,
      paramsOnGalaxy: {
        s3BucketName: inputValues.bucketName,
        realmId: inputValues.realmId,
      },
    };
  };

  const {
    inputValues,
    handleInputChangeV2,
    togglePasswordVisibility,
    isProvisioningIntegration,
    handleToggleAdvanced,
    showAdvancedSetting,
    resetServerAddress,
    handleCheckboxChange,
    provisionIntegrationError,
    provisionIntegrationData,
    handleForceProvision,
    handleProvision,
  } = useProvisionModalFields({
    teamId,
    targetService,
    formatProvisionPayload,
  });

  const ModalBody = (
    <ModalBodyFieldContainer>
      <FieldsContainer>
        <DataBaseNameField onChange={handleInputChangeV2} databaseName={inputValues.databaseName} />
        <PasswordField
          onChange={handleInputChangeV2}
          onClickVisibilityIcon={togglePasswordVisibility}
          showPassword={inputValues.showPassword}
          password={inputValues.password}
        />
        <UsernameField onChange={handleInputChangeV2} username={inputValues.username} />
        <Box>
          <TagsListSectionWithInput
            tagList={companyIds}
            onDelete={removeCompanyId}
            label="Company List"
            type="number"
            inputRef={companyListRef}
            onKeyDown={addCompanyId}
            helperText={'List of company (Co) ids to fetch data from'}
          />
        </Box>
      </FieldsContainer>

      {showAdvancedSetting ? (
        <AdvancedFields
          onClickUndo={resetServerAddress}
          bakIntegration={inputValues.bakIntegration}
          bucketName={inputValues.bucketName}
          serverAddress={inputValues.serverAddress}
          realmId={inputValues.realmId}
          handleCheckboxChange={handleCheckboxChange}
          onChange={handleInputChangeV2}
        />
      ) : null}

      <ProvisionSection
        onProvisionClick={handleProvision}
        onForceProvision={handleForceProvision}
        disableProvisionButton={isProvisioningIntegration}
        isLoading={isProvisioningIntegration}
        handleToggleAdvanced={handleToggleAdvanced}
        showAdvancedSetting={showAdvancedSetting}
      />

      <ProvisionModalResponseSection
        isLoading={isProvisioningIntegration}
        error={provisionIntegrationError}
        responseData={provisionIntegrationData}
        teamId={teamId}
        targetService={targetService}
      />
    </ModalBodyFieldContainer>
  );

  return (
    <BaseProvisionModal
      teamId={teamId}
      body={ModalBody}
      toggle={toggle}
      targetService={targetService}
      isRequesting={isProvisioningIntegration}
    />
  );
};
